class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this)
		);
		this.setHeaderCartIconAccessibility();

		subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
			this.loadPairsWellWith();
		});
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");

		cartLink.addEventListener("click", (event) => {
			event.preventDefault();
			this.open(cartLink);
		});

		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);

		this.loadPairsWellWith();

		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add("animate", "active");
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		document.body.classList.add("cart-overflow-hidden");
		document.body.classList.add("overflow-hidden");
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("cart-overflow-hidden");
		document.body.classList.remove("overflow-hidden");
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			if (!sectionElement) return;
			const html = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector
			);
			if (html) sectionElement.innerHTML = html;
		});

		setTimeout(() => {
			this.querySelector("#CartDrawer-Overlay").addEventListener(
				"click",
				this.close.bind(this)
			);
			this.open();
		});
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		if (!html) return "";
		const el = new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
		return el ? el.innerHTML : "";
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}

	async loadPairsWellWith() {
		const container = this.querySelector("#CartDrawer-PairsWellWith");
		if (!container) return;

		let cart;
		try {
			const res = await fetch("/cart.js");
			cart = await res.json();
		} catch (e) {
			return;
		}

		if (!cart.item_count) {
			container.innerHTML = "";
			return;
		}

		const cartProductIds = [...new Set(cart.items.map((item) => item.product_id))];
		const cartProductIdSet = new Set(cartProductIds);

		const results = await Promise.all(
			cartProductIds.map((id) =>
				fetch(
					`/recommendations/products.json?product_id=${id}&intent=complementary&limit=4`
				)
					.then((r) => r.json())
					.then((data) => data.products || [])
					.catch(() => [])
			)
		);

		const seen = new Set();
		const recommendations = [];
		for (const products of results) {
			for (const product of products) {
				if (!seen.has(product.id) && !cartProductIdSet.has(product.id)) {
					seen.add(product.id);
					recommendations.push(product);
					if (recommendations.length >= 4) break;
				}
			}
			if (recommendations.length >= 4) break;
		}

		if (!recommendations.length) {
			container.innerHTML = "";
			return;
		}

		const heading = container.dataset.heading || "Pairs well with";

		// Build DOM safely — no innerHTML with API data
		const inner = document.createElement("div");
		inner.className = "cart-drawer__pairs-inner";

		const headingEl = document.createElement("p");
		headingEl.className = "cart-drawer__pairs-heading";
		headingEl.textContent = heading;
		inner.appendChild(headingEl);

		const grid = document.createElement("ul");
		grid.className = "cart-drawer__pairs-grid list-unstyled";
		recommendations.forEach((p) => grid.appendChild(this.renderPairsCard(p)));
		inner.appendChild(grid);

		container.innerHTML = "";
		container.appendChild(inner);
	}

	renderPairsCard(product) {
		const imgSrc =
			product.featured_image
				? this.getSizedImageUrl(product.featured_image, "400x")
				: product.images && product.images.length
				? this.getSizedImageUrl(product.images[0].src, "400x")
				: "";
		const price = this.formatMoney(product.price);
		const variant = product.variants && product.variants[0];
		const hasOnlyDefault = product.variants && product.variants.length === 1;

		const li = document.createElement("li");
		li.className = "cart-drawer__pairs-card";

		// Image link
		const imgLink = document.createElement("a");
		imgLink.href = product.url;
		imgLink.className = "cart-drawer__pairs-card-image-link";
		imgLink.setAttribute("tabindex", "-1");
		imgLink.setAttribute("aria-hidden", "true");

		if (imgSrc) {
			const imgWrap = document.createElement("div");
			imgWrap.className = "cart-drawer__pairs-card-image";
			const img = document.createElement("img");
			img.src = imgSrc;
			img.alt = product.title;
			img.width = 200;
			img.height = 200;
			img.loading = "lazy";
			imgWrap.appendChild(img);
			imgLink.appendChild(imgWrap);
		}
		li.appendChild(imgLink);

		// Info area
		const info = document.createElement("div");
		info.className = "cart-drawer__pairs-card-info";

		const titleLink = document.createElement("a");
		titleLink.href = product.url;
		titleLink.className = "cart-drawer__pairs-card-title";
		titleLink.textContent = product.title;
		info.appendChild(titleLink);

		const footer = document.createElement("div");
		footer.className = "cart-drawer__pairs-card-footer";

		const priceEl = document.createElement("span");
		priceEl.className = "cart-drawer__pairs-card-price";
		priceEl.textContent = price;
		footer.appendChild(priceEl);

		const addBtn = document.createElement("button");
		addBtn.type = "button";
		addBtn.className = "cart-drawer__pairs-add";
		addBtn.dataset.variantId = variant ? String(variant.id) : "";
		addBtn.dataset.hasOnlyDefault = String(hasOnlyDefault);
		addBtn.dataset.productUrl = product.url;
		addBtn.setAttribute("aria-label", `Add ${product.title} to cart`);
		addBtn.textContent = "+";
		addBtn.addEventListener("click", (e) => {
			e.preventDefault();
			if (!addBtn.dataset.variantId) {
				window.location.href = product.url;
				return;
			}
			this.addToCartFromPairs(addBtn.dataset.variantId);
		});
		footer.appendChild(addBtn);

		info.appendChild(footer);
		li.appendChild(info);

		return li;
	}

	getSizedImageUrl(url, size) {
		if (!url) return "";
		const parts = url.split("?");
		const query = parts.length > 1 ? "?" + parts[1] : "";
		return parts[0].replace(/(\.[^.]+)$/, `_${size}$1`) + query;
	}

	formatMoney(cents) {
		const amount = (cents / 100).toFixed(2).replace(".", ",");
		return `${amount}\u20ac`;
	}

	async addToCartFromPairs(variantId) {
		const formData = new FormData();
		formData.append("id", variantId);
		formData.append("quantity", "1");

		try {
			const response = await fetch(
				(window.routes && window.routes.cart_add_url) || "/cart/add.js",
				{
					method: "POST",
					headers: {
						"X-Requested-With": "XMLHttpRequest",
						Accept: "application/javascript",
					},
					body: formData,
				}
			);
			const result = await response.json();
			if (result.status) {
				console.error("Failed to add to cart:", result.description);
				return;
			}
			await this.refreshCartDisplay();
		} catch (e) {
			console.error("Failed to add to cart", e);
		}
	}

	async refreshCartDisplay() {
		try {
			const sectionsParam = this.getSectionsToRender()
				.map((s) => s.id)
				.join(",");
			const res = await fetch(
				`${window.location.pathname}?sections=${sectionsParam}`
			);
			const sections = await res.json();

			this.getSectionsToRender().forEach((section) => {
				const sectionElement = section.selector
					? document.querySelector(section.selector)
					: document.getElementById(section.id);
				if (sectionElement && sections[section.id]) {
					sectionElement.innerHTML = this.getSectionInnerHTML(
						sections[section.id],
						section.selector
					);
				}
			});

			// Re-attach overlay listener since innerHTML was replaced
			const overlay = this.querySelector("#CartDrawer-Overlay");
			if (overlay) overlay.addEventListener("click", this.close.bind(this));

			this.loadPairsWellWith();
		} catch (e) {
			console.error("Failed to refresh cart display", e);
		}
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					"input",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, {
							...fetchConfig(),
							...{ body },
						});
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}
